import { useState, useEffect, useContext } from "react";
// SAST Note: uuid4() is used ONLY for React component keys (non-security-critical).
// It is NOT used for generating tokens, passwords, API keys, or any security-sensitive values.
import { v4 as uuid4 } from "uuid";
import {
  Box,
  Skeleton,
  CardContent,
  Tab,
  Tabs,
  IconButton,
  TableCell,
  TableRow,
} from "@mui/material";
import {
  People,
  Chat,
  Psychology,
  QueryStats,
  Group,
} from "@mui/icons-material";
import { AuthContext } from "core/contexts/auth/auth";
import {
  ConversationType,
  MessageType,
} from "modules/chat/components/chat.type";
import { useAppTheme } from "core/contexts/theme/theme";
import { Eye } from "assets/Eye";
import {
  CustomTooltipStyle,
  KipCardBannerStyle,
  KipCardStyle,
  KipCardValue,
} from "./dashboard/dashboard.styled";
import { useNavigate } from "react-router-dom";
import { OverviewTab } from "./dashboard/home";
import { UserAnalyticsTab } from "./dashboard/users/home";
import { ConversationAnalyticsTab } from "./dashboard/conversations/home";
import { ModelPerformanceTab } from "./dashboard/iaus/home";
import { ApiUsageTab } from "./dashboard/apiKeys/home";
import { KipCardTitle } from "./dashboard/dashboard.styled";
import AdminFeedback from "./dashboard/iaus/feedback/feedback";
import AdminInvites from "./dashboard/invites/invites";
import AdminGroups from "./dashboard/groups/groups";
import AdminReports from "./dashboard/reports/reports";
import { FeedbackIcon } from "assets/FeedbackIcon";
import { ReportIcon } from "assets/ReportIcon";
import { ApiIcon } from "assets/ApiIcon";
import { CustomTabs } from "./admin.styled";

// Helper function for cryptographically secure random number generation
const getSecureRandom = (): number => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

interface DashboardData {
  users: any[];
  conversations: any[];
  messages: any[];
  modelUsage: any[];
  apiKeys: any[];
  feedback: any[];
  newUsers: any[];
  averageConversationLength: any[];
  modelDistribution: any[];
  userGroups: any[];
  feedbackDistribution: any[];
  apiKeyUsage: any[];
}

// Main Dashboard Component
const AdminDashboard = () => {
  const { apptheme } = useAppTheme();
  const { checkAuthToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState<any>({
    users: [],
    conversations: [],
    messages: [],
    modelUsage: [],
    apiKeys: [],
    feedback: [],
    newUsers: [],
    averageConversationLength: [],
    modelDistribution: [],
    userGroups: [],
    feedbackDistribution: [],
    apiKeyUsage: [],
  });

  const getAllMessages = (conversations: ConversationType[]): MessageType[] => {
    return conversations.flatMap((conversation) => conversation.messages);
  };

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      // In production, you would make actual API calls here
      // These would be the endpoints you need to create or already have
      try {
        // const conversations = await fetchConversations();
        // const messages = getAllMessages(conversations);
        // const modelUsage = getModels(conversations);
        // console.log("modelUsage:", modelUsage);

        // Simulated API calls with a delay
        setDashboardData({
          // conversations: conversations,
          // messages: messages,
          modelUsage: generateModelUsageData(),
          // apiKeys: apiKeys,
          feedback: generateDummyFeedback(100),
          // averageConversationLength: generateConversationLengthData(),
          // modelDistribution: modelUsage,
          // feedbackDistribution: feedbackDistribution,
          apiKeyUsage: generateApiKeyUsageData(),
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box className="flex-1 px-2">
      <CustomTabs value={tabValue} onChange={handleTabChange} className="mb-3">
        <Tab label="Overview" icon={<QueryStats />} iconPosition="start" />
        <Tab label="API Usage" icon={<ApiIcon />} iconPosition="start" />
        <Tab label="User Analytics" icon={<People />} iconPosition="start" />
        <Tab label="Model Performance" icon={<Psychology />} iconPosition="start"/>
        <Tab label="Feedback" icon={<FeedbackIcon />} iconPosition="start" />
        <Tab label="Invites" icon={<People />} iconPosition="start" />
        <Tab label="Groups" icon={<People />} iconPosition="start" />
        <Tab label="Reports" icon={<ReportIcon />} iconPosition="start" />
      </CustomTabs>

      {tabValue === 0 && <OverviewTab />}
      {tabValue === 1 && <ApiUsageTab />}
      {tabValue === 2 && <UserAnalyticsTab />}
      {tabValue === 3 && <ModelPerformanceTab />}
      {tabValue === 4 && <AdminFeedback />}
      {tabValue === 5 && <AdminInvites />}
      {tabValue === 6 && <AdminGroups />}
      {tabValue === 7 && <AdminReports />}
    </Box>
  );
};

export const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length > 0) {
    return (
      <CustomTooltipStyle elevation={2}>
        {payload.map((line) => {
          // uuid4() used here only for React keys during rendering, not for security purposes
          return (
            <div key={uuid4()}>
              <p className="label">{`${line.name} : ${line.value}`}</p>
            </div>
          );
        })}
      </CustomTooltipStyle>
    );
  }
  return null;
};

// KPI Card Component
export const KpiCard = ({ title, value, icon, loading, navigateTo }) => {
  const navigate = useNavigate();

  const handleOnClick = () => {
    if (navigateTo !== null) {
      navigate(navigateTo);
    }
  };

  return (
    <KipCardStyle className="p-2">
      {/* Calculate the box size: 8px padding, 24px gap x 2, 360px container size, individual = 148px */}
      <div
        className="d-flex flex-column"
        style={{ minHeight: "calc((360px - 24px - 24px - 16px)/2)" }}
      >
        <KipCardBannerStyle>
          {icon}
          <IconButton
            aria-label="close"
            className="p-0 transparent-fill"
            onClick={() => handleOnClick()}
          >
            {navigateTo && <Eye />}
          </IconButton>
        </KipCardBannerStyle>
        {loading || value === undefined ? (
          <div className="d-flex flex-column mx-3 my-2 flex-1 justify-content-evenly">
            <Skeleton variant="text" sx={{ height: "26.5px" }} width="20%" />
            <Skeleton
              variant="text"
              sx={{ height: "21px", marginTop: "8px" }}
              width="60%"
            />
          </div>
        ) : (
          <div className="d-flex flex-column mx-3 my-2 flex-1 justify-content-evenly">
            <KipCardValue>{value.toLocaleString()}</KipCardValue>

            <KipCardTitle>{title}</KipCardTitle>
          </div>
        )}
      </div>
    </KipCardStyle>
  );
};

export const tableRowsLoader = ({ rowsNum }) => {
  return [...Array(rowsNum)].map((row, index) => (
    <TableRow key={index}>
      <TableCell component="th" scope="row">
        <Skeleton animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton animation="wave" variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton animation="wave" variant="text" />
      </TableCell>
    </TableRow>
  ));
};

// Helper Functions and Constants
export const generateRainbowColors = (numColors: number): string[] => {
  const colors: any = [];
  for (let i = 0; i < numColors; i++) {
    const hue = (i * 360) / numColors;
    const color: string = `hsl(${hue}, 70%, 60%)`;
    colors.push(color);
  }
  console.log(`colors: ${JSON.stringify(colors)}`)
  return colors;
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return (
    date.toLocaleDateString() +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

const generateDummyFeedback = (count) => {
  const feedback: any = [];
  const models = ["gpt-4", "gpt-3.5-turbo", "claude-3"];
  const notes = [
    "Great response!",
    "Not what I was looking for",
    "Hallucinated information",
    "Perfect answer",
    "Too verbose",
    null,
  ];

  for (let i = 0; i < count; i++) {
    const createdAt = new Date();
    createdAt.setDate(createdAt.getDate() - Math.floor(getSecureRandom() * 30));

    feedback.push({
      negative: getSecureRandom() > 0.7,
      model: models[Math.floor(getSecureRandom() * models.length)],
      chatid: `chat-${Math.floor(getSecureRandom() * 200)}`,
      conversationid: `conv-${Math.floor(getSecureRandom() * 100)}`,
      note: notes[Math.floor(getSecureRandom() * notes.length)],
      createdAt,
    });
  }

  return feedback;
};

const generateModelUsageData = () => {
  const data: any = [];
  const today = new Date();

  for (let i = 30; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    const dateString = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

    data.push({
      date: dateString,
      "gpt-4": Math.floor(getSecureRandom() * 40) + 20,
      "gpt-3.5-turbo": Math.floor(getSecureRandom() * 60) + 40,
      "claude-3": Math.floor(getSecureRandom() * 30) + 10,
    });
  }

  return data;
};

const generateApiKeyUsageData = () => {
  const data: any = [];
  const today = new Date();

  for (let i = 30; i >= 0; i--) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    const dateString = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const usage = Math.floor(getSecureRandom() * 500) + 100;

    data.push({
      date: dateString,
      usage,
    });
  }

  return data;
};

export default AdminDashboard;
